import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, BookOpen, Calendar, BarChart, DollarSign, Pencil } from 'lucide-react';
import { useRoyaltyReport } from '@/hooks/useRoyaltyReports';
import { useQuarterlyReports } from '@/hooks/useQuarterlyReports';
import { formatDate, formatCurrency } from '@/utils/formatters';
import StatusBadge from '@/components/common/StatusBadge';
import { useAuthor } from '@/hooks/useAuthors';
import { usePDFGeneration } from '@/hooks/usePDFGeneration';
import { useToast } from '@/hooks/use-toast';
import QuarterlySalesBreakdown from '@/components/admin/QuarterlySalesBreakdown';

const RoyaltyReportView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleDownloadPDF } = usePDFGeneration();
  const { toast } = useToast();
  
  const { data: report, isLoading } = useRoyaltyReport(id || '');
  const { data: author } = useAuthor(report?.authorId || '');
  
  // Get all reports for this quarter to show breakdown per book
  const { data: quarterlyReports } = useQuarterlyReports(
    report?.authorId || '', 
    report?.year || 0, 
    report?.quarter || 0
  );
  
  // Filter quarterly reports to only include the specific quarter and year
  const currentQuarterReports = quarterlyReports?.filter(r => 
    r.quarter === report?.quarter && r.year === report?.year
  ) || [];

  // Calculate totals for the quarter using the new calculation logic
  const totalSales = currentQuarterReports.reduce((sum, r) => sum + (r.unitsSold || r.salesCount), 0) || report?.unitsSold || report?.salesCount;
  const totalGrossSales = currentQuarterReports.reduce((sum, r) => sum + (r.grossSales || r.amount), 0) || report?.grossSales || report?.amount;
  
  // Calculate total previous royalty income (using netSales field)
  const totalPreviousRoyaltyIncome = currentQuarterReports.reduce((sum, r) => sum + (r.netSales || 0), 0) || report?.netSales || 0;
  
  // Calculate total royalty amount using new formula: Gross Sales + Previous Royalty Income
  const totalRoyaltyAmount = currentQuarterReports.reduce((sum, r) => sum + (r.royaltyAmount || r.amount), 0) || report?.royaltyAmount || report?.amount;
  
  // Count unique book titles instead of total reports
  const uniqueBookTitles = new Set(
    currentQuarterReports
      .map(r => r.books?.title)
      .filter(title => title)
  );
  const bookCount = uniqueBookTitles.size || (report?.books?.title ? 1 : 0);

  const onDownloadPDF = async () => {
    if (!report) return;
    
    await handleDownloadPDF(
      report,
      currentQuarterReports,
      totalSales,
      totalRoyaltyAmount,
      totalGrossSales,
      0, // removed totalReturns
      totalPreviousRoyaltyIncome,
      author?.name || 'Unknown Author'
    );
  };
  
  if (isLoading) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center items-center h-full">
          <p>Loading royalty report...</p>
        </div>
      </MainLayout>
    );
  }
  
  if (!report) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-2">Report Not Found</h2>
          <p className="mb-4">The royalty report you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/admin/royalties')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout allowedRoles={['admin']}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/royalties')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <PageHeader
            title={`Q${report.quarter} ${report.year} Royalty Report`}
            description={`Report for ${author?.name || 'Unknown Author'}`}
          />
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate(`/admin/royalties/${report.id}/edit`)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit Report
          </Button>
          <Button onClick={onDownloadPDF} className="flex gap-2">
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Report Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  Author
                </dt>
                <dd className="w-2/3 text-sm">{author?.name || 'Unknown'}</dd>
              </div>
              
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  <Calendar className="h-4 w-4" />
                  Report Period
                </dt>
                <dd className="w-2/3 text-sm">Q{report.quarter} {report.year}</dd>
              </div>
              
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  Report Date
                </dt>
                <dd className="w-2/3 text-sm">{formatDate(report.createdAt)}</dd>
              </div>
              
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  Status
                </dt>
                <dd className="w-2/3 text-sm">
                  <StatusBadge status={report.status} />
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quarter Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  <BookOpen className="h-4 w-4" />
                  Total Books
                </dt>
                <dd className="w-2/3 text-sm">{bookCount}</dd>
              </div>
              
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  <BarChart className="h-4 w-4" />
                  Total Units Sold
                </dt>
                <dd className="w-2/3 text-sm">{totalSales}</dd>
              </div>
              
              <div className="flex flex-row">
                <dt className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-1/3">
                  <DollarSign className="h-4 w-4" />
                  Total Amount
                </dt>
                <dd className="w-2/3 text-sm font-medium text-lg">{formatCurrency(totalRoyaltyAmount)} {report.currency}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
      
      <QuarterlySalesBreakdown
        quarterlyReports={currentQuarterReports}
        fallbackReport={report}
        totalSales={totalSales}
        totalGrossSales={totalGrossSales}
        totalRoyaltyAmount={totalRoyaltyAmount}
      />
    </MainLayout>
  );
};

export default RoyaltyReportView;
